import React, { useState, useEffect } from 'react';
import { auth } from '../services/api';
import './Login.css';
import MicrosoftLogo from './MicrosoftLogo';

function ChangePassword({ user, onSuccess }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [msAvailable, setMsAvailable] = useState(false);
  const [msLoading, setMsLoading] = useState(false);

  // Bouton Microsoft proposé si le serveur l'a configuré : un compte relié à
  // Microsoft n'a pas besoin de mot de passe CRM (même parcours que Login.js).
  useEffect(() => {
    let mounted = true;
    auth.getConfig()
      .then((res) => { if (mounted) setMsAvailable(!!res.data.microsoft); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const handleMicrosoft = async () => {
    setError('');
    setMsLoading(true);
    try {
      const res = await auth.microsoftStart();
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.error || 'Connexion Microsoft indisponible');
      setMsLoading(false);
    }
  };

  const strict = !!user.bypass_ip;
  const hasMinLength = newPassword.length >= (strict ? 8 : 6);
  const hasUppercase = !strict || /[A-Z]/.test(newPassword);
  const hasSpecial = !strict || /[^a-zA-Z0-9]/.test(newPassword);
  const passwordValid = hasMinLength && hasUppercase && hasSpecial;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!passwordValid) {
      setError('Le mot de passe ne respecte pas les critères de sécurité');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      setLoading(true);
      await auth.changePassword(null, newPassword);

      // Mettre à jour le user dans le localStorage
      const updatedUser = { ...user, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      onSuccess(updatedUser);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-muted)'
    }}>
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '40px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '22px', color: 'var(--text-primary)' }}>
          Changement de mot de passe
        </h2>
        <p style={{ margin: '0 0 24px', color: 'var(--text-secondary)', fontSize: '14px' }}>
          Bienvenue {user.prenom}. Vous devez choisir un nouveau mot de passe pour continuer.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Nouveau mot de passe
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={strict ? '8' : '6'}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            {newPassword && strict && (
              <div style={{ marginTop: '8px', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ color: hasMinLength ? 'var(--color-success)' : 'var(--text-muted)' }}>{hasMinLength ? '✓' : '○'} 8 caractères minimum</span>
                <span style={{ color: hasUppercase ? 'var(--color-success)' : 'var(--text-muted)' }}>{hasUppercase ? '✓' : '○'} Une majuscule</span>
                <span style={{ color: hasSpecial ? 'var(--color-success)' : 'var(--text-muted)' }}>{hasSpecial ? '✓' : '○'} Un caractère spécial</span>
              </div>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Confirmer le mot de passe
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={strict ? '8' : '6'}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            {newPassword && confirmPassword && newPassword === confirmPassword && (
              <small style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))', fontWeight: '600' }}>Les mots de passe correspondent</small>
            )}
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <small style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))', fontWeight: '600' }}>Les mots de passe ne correspondent pas</small>
            )}
          </div>

          {error && (
            <div style={{
              padding: '10px',
              background: 'var(--color-error-light)',
              border: '1px solid light-dark(#fecaca, var(--border-strong))',
              borderRadius: '6px',
              color: 'var(--color-error)',
              fontSize: '13px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !passwordValid || !confirmPassword || newPassword !== confirmPassword}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--color-info)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Modification...' : 'Valider le nouveau mot de passe'}
          </button>
        </form>

        {msAvailable && (
          <>
            <div style={{ margin: '20px 0 12px', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>ou</div>
            <button type="button" className="login-ms-button" onClick={handleMicrosoft} disabled={msLoading}>
              {msLoading ? (
                <>
                  <span className="spinner" />
                  Redirection vers Microsoft…
                </>
              ) : (
                <>
                  <MicrosoftLogo />
                  Se connecter avec Microsoft
                </>
              )}
            </button>
            <p className="login-ms-hint">Utilisez votre adresse e-mail professionnelle.</p>
          </>
        )}
      </div>
    </div>
  );
}

export default ChangePassword;
